import React, { useState, useEffect } from 'react';
import {
    Input,
    Button,
    Spinner,
    Link,
    SharedSelection,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
} from '@heroui/react';
import {
    EyeIcon,
    EyeSlashIcon,
    GlobeAltIcon,
    ArrowUpRightIcon,
} from '@heroicons/react/24/outline';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLinkedInAuthUrl, handleOAuthSignups, loginWithNormalCredentials } from '../../services/api';
import { LoginDto, LoginFormData, OAuthProvider } from '../../types/types';
import { toast } from '../../utils/toast';
import hoowlrLogo from '../../assets/images/logo.svg';
import loginBanner from '../../assets/images/login-banner.png';

const authInputClassNames = {
    base: 'gap-1',
    label:
        '!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0',
    inputWrapper:
        'h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]',
    input: 'text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]',
    errorMessage: 'text-left ml-1 w-full text-xs',
};

const socialButtonClassName =
    'h-11 min-w-0 flex-1 rounded-xl lg:rounded-md border border-slate-200 lg:border-[#EBEBEC] bg-white p-1 shadow-xs hover:bg-slate-50 hover:border-slate-300 active:scale-[0.98] transition-all flex items-center justify-center';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <div className="text-red-500 text-center">Login failed. Please try again.</div>;
        }
        return this.props.children;
    }
}

const Login: React.FC = () => {
    const { t, i18n } = useTranslation('login');
    const [formData, setFormData] = useState<LoginFormData>({
        email: '',
        password: '',
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [navigate, setNavigate] = useState<boolean>(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(localStorage.getItem('i18nextLng') || 'en');

    const languageNames: { [key: string]: string } = {
        en: t('language_en'),
        de: t('language_de'),
        hr: t('language_hr'),
    };

    const handleLanguageChange = (keys: SharedSelection) => {
        let value: string;
        if (typeof keys === 'string') {
            value = keys;
        } else if ('currentKey' in keys && typeof keys.currentKey === 'string') {
            value = keys.currentKey;
        } else {
            value = 'en';
        }
        setSelectedLanguage(value);
        i18n.changeLanguage(value);
        localStorage.setItem('i18nextLng', value);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const oauthError = urlParams.get('oauth_error');
        if (oauthError) {
            toast.error(t('error_auth_failed'));
        }
    }, [t]);

    const togglePasswordVisibility = (): void => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    const handleEmailChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, email: value }));
        setErrors((prev) => ({ ...prev, email: '' }));
    };

    const handlePasswordChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, password: value }));
        setErrors((prev) => ({ ...prev, password: '' }));
    };

    const validateEmail = (value: string): string | null => {
        if (!value) {
            return t('email_required');
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return t('email_invalid');
        }
        return null;
    };

    const validatePassword = (value: string): string | null => {
        if (!value) {
            return t('password_required');
        }
        if (value.length < 8) {
            return t('password_min_length');
        }
        return null;
    };

    const handleOAuth = (provider: OAuthProvider): void => {
        if (loading) return;
        setLoading(true);
        try {
            handleOAuthSignups(provider);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || t('error_oauth_failed', { provider });
            toast.error(errorMessage);
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        const emailError = validateEmail(formData.email);
        const passwordError = validatePassword(formData.password);

        if (emailError || passwordError) {
            setErrors({
                email: emailError || '',
                password: passwordError || '',
            });
            return;
        }

        setLoading(true);

        try {
            const loginData: LoginDto = {
                username: formData.email,
                password: formData.password,
            };

            const response = await loginWithNormalCredentials(loginData);

            if (response) {
                localStorage.setItem('loginResponse', JSON.stringify(response));
                localStorage.setItem('i18nextLng', JSON.stringify(response.preferredLanguage));

                toast.success(t('login_success') || 'Login successful!');

                setTimeout(() => {
                    setLoading(false);
                    setNavigate(true);
                }, 1000);
            }
        } catch (err: any) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                t('error_login_failed');

            toast.error(errorMessage);
            setLoading(false);
        }
    };

    if (navigate) {
        return <Navigate to="/dashboard" state={formData} replace />;
    }

    const handleLinkedInLogin = async (): Promise<void> => {
        if (loading) return;
        setLoading(true);
        try {
            const url = await getLinkedInAuthUrl();
            if (!url || typeof url !== 'string') {
                throw new Error('Invalid LinkedIn authorization URL');
            }
            window.location.href = url;
        } catch (error) {
            toast.error(t('error_linkedin_failed'));
            setLoading(false);
        }
    };

    return (
        <ErrorBoundary>
            <div className="login-page flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
                <div className="flex min-w-0 flex-1 flex-col">
                    <div
                        className="
                            flex min-h-dvh flex-col justify-between
                            px-6 py-6 sm:px-12 sm:py-8
                            lg:py-9
                            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
                            [@media(max-height:700px)]:lg:py-3
                        "
                    >
                        <div className="flex w-full items-center justify-between">
                            <img
                                src={hoowlrLogo}
                                alt="Hoowlr"
                                className="
                                    h-9 w-auto object-contain object-left
                                    lg:h-16 lg:w-48
                                    [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                                    [@media(max-height:700px)]:lg:h-10
                                "
                            />
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        variant="light"
                                        size="sm"
                                        className="h-8 lg:h-9 gap-1.5 text-xs lg:text-sm font-medium text-[#71717A] font-['Plus_Jakarta_Sans',sans-serif] rounded-xl border border-slate-200/80 lg:border-transparent"
                                        startContent={<GlobeAltIcon className="h-4 w-4 lg:h-5 lg:w-5 text-slate-500" />}
                                        aria-label={t('language_select_label')}
                                    >
                                        {languageNames[selectedLanguage]}
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                    itemClasses={{
                                    base: "rounded-xl data-[focus-visible=true]:outline-0 data-[focus-visible=true]:ring-0 data-[focus-visible=true]:ring-offset-0 data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:bg-[#EBEBEC]",
                                }}
                                    aria-label="Language selection"
                                    onSelectionChange={handleLanguageChange}
                                    selectionMode="single"
                                    selectedKeys={[selectedLanguage]}
                                >
                                    <DropdownItem key="en">{t('language_en')}</DropdownItem>
                                    <DropdownItem key="de">{t('language_de')}</DropdownItem>
                                    <DropdownItem key="hr">{t('language_hr')}</DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </div>

                        <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
                            <h1
                                className="
                                    text-center text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B] mb-3 sm:mb-4 lg:mb-8
                                    [@media(max-height:700px)]:mb-2
                                "
                            >
                                {t('welcome_title')}
                            </h1>

                            <div
                                className="
                                    flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                                    [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                                    [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
                                "
                            >
                                <div className="flex flex-col gap-1 sm:gap-2 [@media(max-height:700px)]:gap-0.5 text-left">
                                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold leading-6 lg:leading-8 text-[#18181B]">{t('login_in_title')}</h2>
                                    <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">{t('sign_in_subtitle')}</p>
                                </div>

                                <form
                                    noValidate
                                    className="
                                        flex flex-col gap-4 lg:gap-6
                                        [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                                        [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
                                    "
                                    onSubmit={handleSubmit}
                                >
                                    <div
                                        className="
                                            flex flex-col gap-3 lg:gap-4
                                            [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                                        "
                                    >
                                        <Input
                                            type="email"
                                            name="email"
                                            label={t('email_label')}
                                            labelPlacement="outside"
                                            placeholder={t('email_placeholder')}
                                            value={formData.email}
                                            onValueChange={handleEmailChange}
                                            className="w-full"
                                            classNames={authInputClassNames}
                                            size="md"
                                            validate={validateEmail}
                                            isInvalid={!!errors.email}
                                            errorMessage={errors.email}
                                        />

                                        <Input
                                            type={isPasswordVisible ? 'text' : 'password'}
                                            name="password"
                                            label={t('password_label')}
                                            labelPlacement="outside"
                                            placeholder={t('password_placeholder')}
                                            value={formData.password}
                                            onValueChange={handlePasswordChange}
                                            className="w-full"
                                            classNames={authInputClassNames}
                                            endContent={
                                                <button
                                                    className="focus:outline-none"
                                                    type="button"
                                                    onClick={togglePasswordVisibility}
                                                    aria-label="toggle password visibility"
                                                >
                                                    {isPasswordVisible ? (
                                                        <EyeSlashIcon className="h-4 w-4 text-gray-400 pointer-events-none" />
                                                    ) : (
                                                        <EyeIcon className="h-4 w-4 text-gray-400 pointer-events-none" />
                                                    )}
                                                </button>
                                            }
                                            size="md"
                                            validate={validatePassword}
                                            isInvalid={!!errors.password}
                                            errorMessage={errors.password}
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        isLoading={loading}
                                        disabled={loading}
                                        spinner={<Spinner size="sm" color="white" />}
                                        className="h-10 lg:h-10 w-full rounded-2xl lg:rounded-3xl bg-[#0485F7] text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm"
                                    >
                                        {loading ? '' : t('sign_in_button')}
                                    </Button>
                                </form>

                                <div className="flex items-center">
                                    <div className="h-px flex-1 bg-[#EBEBEC]" />
                                    <span className="px-3 text-[11px] sm:text-xs font-medium leading-4 text-[#71717A]">
                                        {t('or_continue_with')}
                                    </span>
                                    <div className="h-px flex-1 bg-[#EBEBEC]" />
                                </div>

                                <div className="flex gap-3">
                                    <Button
                                        isIconOnly
                                        onPress={() => handleOAuth('google')}
                                        isDisabled={loading}
                                        className={socialButtonClassName}
                                        aria-label={t('google_button')}
                                    >
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1.01.68-2.3 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C4.01 20.49 7.76 23 12 23z" />
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.76 1 4.01 3.51 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                            <path fill="none" d="M1 1h22v22H1z" />
                                        </svg>
                                    </Button>

                                    <Button
                                        isIconOnly
                                        onPress={() => handleOAuth('facebook')}
                                        isDisabled={loading}
                                        className={socialButtonClassName}
                                        aria-label={t('facebook_button')}
                                    >
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path fill="#1877F2" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.892h-2.33v6.987C18.343 21.128 22 16.991 22 12z" />
                                        </svg>
                                    </Button>

                                    <Button
                                        isIconOnly
                                        onPress={handleLinkedInLogin}
                                        isDisabled={loading}
                                        className={socialButtonClassName}
                                        aria-label={t('linkedin_button')}
                                    >
                                        <svg className="h-6 w-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.85-3.037-1.85 0-2.133 1.446-2.133 2.941v5.665H9.357V9.143h3.413v1.561h.048c.475-.898 1.637-1.846 3.365-1.846 3.602 0 4.262 2.369 4.262 5.457v6.137zM5.337 7.582c-1.14 0-2.063-.928-2.063-2.065 0-1.137.923-2.065 2.063-2.065 1.139 0 2.063.928 2.063 2.065 0 1.137-.924 2.065-2.063 2.065zm1.781 12.87H3.556V9.143h3.562v11.309zM22.225 0H1.771C.792 0 0 .792 0 1.771v20.458C0 23.208.792 24 1.771 24h20.458c.979 0 1.771-.792 1.771-1.771V1.771C24 .792 23.208 0 22.225 0z" />
                                        </svg>
                                    </Button>
                                </div>

                                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-2 text-xs sm:text-sm lg:text-base leading-5 lg:leading-6">
                                    <span className="text-[#71717A]">{t('no_account')}</span>
                                    <Link
                                        href="/register"
                                        className="gap-0.5 px-0.5 text-xs sm:text-sm lg:text-base text-[#2563EB] font-semibold lg:font-normal"
                                        underline="hover"
                                    >
                                        {t('register_link')}
                                        <ArrowUpRightIcon className="mt-0.5 h-2.5 w-2.5 opacity-60" />
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <p
                            className="
                                pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]
                            "
                        >
                            {t('footer_copyright', '© Hoowlr 2026, All rights reserved')}
                        </p>
                    </div>
                </div>

                <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
                    <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
                        <img src={loginBanner} alt="" className="max-h-full max-w-full object-contain" />
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default Login;